/**
 * Dashboard page - Main overview of portfolio and system health.
 */

import { useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
} from '@mui/icons-material';
import { portfolioApi, recommendationsApi, riskApi, systemHealthApi } from '../services/api';
import type { PortfolioResponse, Recommendation, RiskRegime, SystemHealth } from '../types';
import { useNavigate } from 'react-router-dom';
import { AIIntelligencePanel } from '../components/AIIntelligencePanel';
import { usePortfolio } from '../contexts/PortfolioContext';

export function DashboardPage() {
  const navigate = useNavigate();
  const { selectedPortfolioId, selectedPortfolio } = usePortfolio();
  const [portfolio, setPortfolio] = useState<PortfolioResponse | null>(null);
  const [recommendations, setRecommendations] = useState<Recommendation[]>([]);
  const [riskRegime, setRiskRegime] = useState<RiskRegime | null>(null);
  const [systemHealth, setSystemHealth] = useState<SystemHealth | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (selectedPortfolioId) {
      loadDashboardData();
    } else {
      // If no portfolio selected, stop loading and show message
      setLoading(false);
      setError('No portfolio selected. Please select a portfolio or create one.');
    }
  }, [selectedPortfolioId]);

  const loadDashboardData = async () => {
    if (!selectedPortfolioId) return;
    
    try {
      setLoading(true);
      setError(null);

      const [portfolioData, recommendationsData, riskData, healthData] = await Promise.all([
        portfolioApi.getCurrentById(selectedPortfolioId),
        recommendationsApi.getOpen(selectedPortfolioId),
        riskApi.getCurrent(),
        systemHealthApi.getHealth().catch(() => null), // Gracefully handle if endpoint not available yet
      ]);

      setPortfolio(portfolioData);
      setRecommendations(recommendationsData.recommendations);
      setRiskRegime(riskData);
      setSystemHealth(healthData || {
        pk: 'GLOBAL',
        schwabMode: 'mock',
        aiEnabled: false,
        tradingRecommendationsEnabled: true,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load dashboard data');
      console.error('Dashboard load error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  const topPositions = portfolio?.positions
    .sort((a, b) => b.currentValue - a.currentValue)
    .slice(0, 5) || [];

  const getRiskRegimeColor = (regime: string) => {
    switch (regime) {
      case 'LOW_RISK':
      case 'NORMAL':
        return 'success';
      case 'CAUTIOUS':
        return 'warning';
      case 'RISK_OFF':
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <Box>
      {/* Portfolio Header */}
      {selectedPortfolio && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="h5" gutterBottom>
                  {selectedPortfolio.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedPortfolio.portfolioType} Portfolio
                  {selectedPortfolio.isPrimary && ' • Primary'}
                </Typography>
              </Box>
              <Tooltip
                title={
                  selectedPortfolio.portfolioType === 'LIVE'
                    ? 'LIVE portfolios represent real capital backed by actual brokerage accounts. Recommendations apply to real money, but trades are executed manually.'
                    : 'SANDBOX portfolios use virtual capital for testing and research. Perfect for exploring strategies without risking real money.'
                }
                arrow
              >
                <Chip
                  label={selectedPortfolio.portfolioType}
                  color={selectedPortfolio.portfolioType === 'LIVE' ? 'primary' : 'default'}
                />
              </Tooltip>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* System Health Section */}
      {systemHealth && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              System Health
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 2 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Last Daily Run
                </Typography>
                <Box>
                  <Typography variant="body2">
                    {systemHealth.lastDailyRunAt
                      ? new Date(systemHealth.lastDailyRunAt).toLocaleString()
                      : 'Never'}
                  </Typography>
                  <Chip
                    label={systemHealth.lastDailyRunStatus || 'unknown'}
                    size="small"
                    color={
                      systemHealth.lastDailyRunStatus === 'success'
                        ? 'success'
                        : systemHealth.lastDailyRunStatus === 'failure'
                        ? 'error'
                        : 'default'
                    }
                    sx={{ mt: 0.5 }}
                  />
                </Box>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Schwab Mode
                </Typography>
                <Box>
                  <Chip
                    label={systemHealth.schwabMode.toUpperCase()}
                    size="small"
                    color={systemHealth.schwabMode === 'live' ? 'success' : 'default'}
                  />
                </Box>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  AI Enabled
                </Typography>
                <Box>
                  <Chip
                    label={systemHealth.aiEnabled ? 'Yes' : 'No'}
                    size="small"
                    color={systemHealth.aiEnabled ? 'success' : 'default'}
                  />
                </Box>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Trading Recommendations
                </Typography>
                <Box>
                  <Chip
                    label={systemHealth.tradingRecommendationsEnabled ? 'Enabled' : 'Disabled'}
                    size="small"
                    color={systemHealth.tradingRecommendationsEnabled ? 'success' : 'error'}
                  />
                </Box>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* AI Intelligence Panel (Phase 7.8) */}
      <AIIntelligencePanel />

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3 }}>
        {/* Portfolio Summary */}
        <Box>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Portfolio Summary
              </Typography>
              {portfolio && (
                <>
                  <Typography 
                    variant="h4" 
                    gutterBottom
                    sx={{ 
                      color: '#F3F4F6', // Primary text color - bright white for readability
                      fontWeight: 600 
                    }}
                  >
                    ${portfolio.portfolio.total_value.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Cash: ${portfolio.portfolio.cash_balance.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </Typography>
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Allocation by Bucket
                    </Typography>
                    {Object.entries(portfolio.portfolio.bucket_allocations).map(([bucket, pct]) => (
                      <Box key={bucket} sx={{ mb: 1 }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2">{bucket}</Typography>
                          <Typography variant="body2" fontWeight="bold">
                            {pct.toFixed(1)}%
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </>
              )}
            </CardContent>
          </Card>
        </Box>

        {/* Risk Regime */}
        <Box>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Risk Regime
              </Typography>
              {riskRegime && (
                <>
                  <Chip
                    label={riskRegime.regime}
                    color={getRiskRegimeColor(riskRegime.regime) as any}
                    sx={{ mb: 2 }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    As of: {new Date(riskRegime.as_of).toLocaleString()}
                  </Typography>
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Key Indicators
                    </Typography>
                    {Object.entries(riskRegime.indicators).map(([key, value]) => (
                      <Typography key={key} variant="body2">
                        {key}: {typeof value === 'number' ? value.toFixed(2) : value}
                      </Typography>
                    ))}
                  </Box>
                </>
              )}
            </CardContent>
          </Card>
        </Box>

        {/* Open Recommendations */}
        <Box sx={{ gridColumn: { xs: '1', md: '1 / -1' } }}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">Open Recommendations</Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => navigate('/recommendations')}
                >
                  View All
                </Button>
              </Box>
              {recommendations.length === 0 ? (
                <Alert severity="info">No open recommendations</Alert>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Ticker</TableCell>
                        <TableCell>Action</TableCell>
                        <TableCell>Reason</TableCell>
                        <TableCell>Suggested</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {recommendations.slice(0, 5).map((rec) => (
                        <TableRow key={rec.recommendationId}>
                          <TableCell>{rec.ticker}</TableCell>
                          <TableCell>
                            <Chip label={rec.actionType} size="small" />
                          </TableCell>
                          <TableCell>{rec.reasonCode}</TableCell>
                          <TableCell>
                            {rec.suggestedShares
                              ? `${rec.suggestedShares} shares`
                              : rec.suggestedNotional
                              ? `$${rec.suggestedNotional.toFixed(2)}`
                              : 'N/A'}
                          </TableCell>
                          <TableCell>
                            <Button
                              size="small"
                              onClick={() => navigate(`/recommendations/${rec.recommendationId}`)}
                            >
                              View
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Box>

        {/* Top Positions */}
        <Box sx={{ gridColumn: { xs: '1', md: '1 / -1' } }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Top 5 Positions
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Ticker</TableCell>
                      <TableCell>Quantity</TableCell>
                      <TableCell>Value</TableCell>
                      <TableCell>Gain %</TableCell>
                      <TableCell>Bucket</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {topPositions.map((position) => (
                      <TableRow key={position.ticker}>
                        <TableCell>{position.ticker}</TableCell>
                        <TableCell>{position.quantity}</TableCell>
                        <TableCell>
                          ${position.currentValue.toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={0.5}>
                            {position.gainPct >= 0 ? (
                              <TrendingUp color="success" fontSize="small" />
                            ) : (
                              <TrendingDown color="error" fontSize="small" />
                            )}
                            {position.gainPct.toFixed(2)}%
                          </Box>
                        </TableCell>
                        <TableCell>{position.bucket}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Box>
      </Box>
    </Box>
  );
}

